// ── Role constants ────────────────────────────────────────────────────────────
export const ROLES = {
  MEMBER:              'Member',            // Employee — self-service pages only
  CLIENT_ADMIN:        'ClientAdmin',       // Admin scoped to a single establishment
  MULTI_CLIENT_ADMIN:  'MultiClientAdmin',  // Admin with access to multiple establishments
  SUPER_ADMIN:         'SuperAdmin',        // Platform admin — all establishments, full access
};

// ─────────────────────────────────────────────────────────────────────────────
// PAGE_PERMISSIONS
//
// Rules:
//  • SuperAdmin         — all pages (platform-wide access)
//  • MultiClientAdmin   — same operational access as ClientAdmin, scoped to their assigned orgs;
//                         uses org-switcher dropdown to select active org
//  • ClientAdmin        — all operational pages, scoped to their single establishment
//  • Member             — self-service only: profile, own payslip/leave/barcode/shift
//
// The backend enforces data scoping.
// ─────────────────────────────────────────────────────────────────────────────

// Shorthand — pages accessible to both ClientAdmin variants
const CA = ['ClientAdmin', 'MultiClientAdmin'];

export const PAGE_PERMISSIONS = {

  // Home
  'dashboard':                ['Member', ...CA, 'SuperAdmin'],
  'reports':                  [...CA, 'SuperAdmin'],
  'ai-assistant':             ['Member', ...CA, 'SuperAdmin'],

  // ── Organization ──────────────────────────────────────────────────────────
  'organizations':            ['SuperAdmin'],
  'add-organization':         ['SuperAdmin'],
  'update-organization':      ['SuperAdmin'],
  'employees':                [...CA, 'SuperAdmin'],
  'new-employee':             [...CA, 'SuperAdmin'],
  'update-employee':          [...CA, 'SuperAdmin'],
  'my-profile':               ['Member', ...CA],

  // ── Workforce / Attendance ─────────────────────────────────────────────────
  'attendance-registry':      [...CA, 'SuperAdmin'],
  'barcode-scanner':          [...CA, 'SuperAdmin'],
  'shift-live-log':           [...CA, 'SuperAdmin'],
  'shift-tracker':            [...CA, 'SuperAdmin'],
  'holiday-schedule':         ['Member', ...CA, 'SuperAdmin'],

  // ── Workforce / Payroll insights ───────────────────────────────────────────
  'monthly-info-service':     [...CA, 'SuperAdmin'],

  // ── Payroll processing ────────────────────────────────────────────────────
  'generate-payroll':         [...CA, 'SuperAdmin'],
  'monthly-payroll-registry': [...CA, 'SuperAdmin'],
  'payslip':                  ['Member', ...CA, 'SuperAdmin'],
  'view-statements':          ['Member', ...CA, 'SuperAdmin'],

  // Financial management
  'adjustments':              [...CA, 'SuperAdmin'],
  'variable-pay':             [...CA, 'SuperAdmin'],

  // ── Statutory Forms ────────────────────────────────────────────────────────
  'forms-templates':          [...CA, 'SuperAdmin'],
  'view-forms':               [...CA, 'SuperAdmin'],
  'form-data-review':         [...CA, 'SuperAdmin'],
  'appointment-order':        [...CA, 'SuperAdmin'],
  'form-s-uti':               [...CA, 'SuperAdmin'],
  'form-xiv':                 [...CA, 'SuperAdmin'],
  'form-19-payslip':          ['Member', ...CA, 'SuperAdmin'],
  'form-t-muster-roll':       [...CA, 'SuperAdmin'],
  'ecr-generator':            [...CA, 'SuperAdmin'],
  'form-x-wages':             [...CA, 'SuperAdmin'],
  'form-xiii-min-wages':      [...CA, 'SuperAdmin'],
  'bank-statement':           [...CA, 'SuperAdmin'],
  'form-xxiii-overtime':      [...CA, 'SuperAdmin'],
  'form-5-earned-leave':      [...CA, 'SuperAdmin'],
  'form-u-annual-returns':    [...CA, 'SuperAdmin'],
  'form-xxix-deductions':     [...CA, 'SuperAdmin'],
  'form-a-professional-tax':  [...CA, 'SuperAdmin'],

  // ── Leave ─────────────────────────────────────────────────────────────────
  // Members apply leave from inside My Attendance — admins still need the standalone page.
  'apply-leave':              [...CA, 'SuperAdmin'],
  'leave-absent-record':      ['Member', ...CA, 'SuperAdmin'],
  'approve-leave':            [...CA, 'SuperAdmin'],
  'analytics':                [...CA, 'SuperAdmin'],
  'apply-resignation':        ['Member', ...CA, 'SuperAdmin'],
  'approve-resignation':      [...CA, 'SuperAdmin'],
  'terminate-employee':       [...CA, 'SuperAdmin'],
  'overtime-work':            ['Member', ...CA, 'SuperAdmin'],

  // ── Finance ───────────────────────────────────────────────────────────────
  'generate-invoice':         [...CA, 'SuperAdmin'],
  'view-invoices':            [...CA, 'SuperAdmin'],
  'assets':                   [...CA, 'SuperAdmin'],

  // ── Compliance ────────────────────────────────────────────────────────────
  'documents':                [...CA, 'SuperAdmin'],

  // ── Admin Centre ──────────────────────────────────────────────────────────
  'admin-dashboard':          ['SuperAdmin'],
  'audit-trail':              ['SuperAdmin'],
  'groups-management':        ['SuperAdmin'],        // Groups + membership management
  'invite-users':             [...CA, 'SuperAdmin'],
  'leave-settings':           [...CA, 'SuperAdmin'],

  // ── Barcode / Shift self-service ──────────────────────────────────────────
  'my-barcode':               ['Member'],
  'my-shift-history':         ['Member'],
  'my-attendance':            ['Member'],
};

/**
 * Returns true if the user has at least one of the given roles.
 */
export function hasRole(user, roles) {
  const userRoles = user?.roles || [];
  return roles.some(r => userRoles.includes(r));
}

/**
 * Returns true if the user is allowed to access the given view/page.
 * Views not listed in PAGE_PERMISSIONS are denied by default.
 */
export function canAccessPage(user, viewId) {
  const allowed = PAGE_PERMISSIONS[viewId];
  if (!allowed) return false;
  return hasRole(user, allowed);
}

/**
 * Returns the highest-privilege role the user has.
 * Priority: SuperAdmin > MultiClientAdmin > ClientAdmin > Member
 */
export function getPrimaryRole(user) {
  const userRoles = user?.roles || [];
  if (userRoles.includes('SuperAdmin'))        return 'SuperAdmin';
  if (userRoles.includes('MultiClientAdmin'))  return 'MultiClientAdmin';
  if (userRoles.includes('ClientAdmin'))       return 'ClientAdmin';
  if (userRoles.includes('Member'))            return 'Member';
  return null;
}

/**
 * Returns true if the user is a client-level admin (ClientAdmin or MultiClientAdmin).
 */
export function isClientLevelAdmin(user) {
  const role = getPrimaryRole(user);
  return role === 'ClientAdmin' || role === 'MultiClientAdmin';
}
